import api from '@/libs/axiosInstance';
import type { ApiResponse, MentionBoostCampaignConflictResponse } from '@fluenceur/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

const MENTION_BOOST_CAMPAIGN_CONFLICT_QUERY_KEY_PREFIX = 'mentionBoostCampaignConflict';

export const mentionBoostCampaignConflictQueryKey = (
  mentionId: string | null | undefined,
  campaignId: string | null | undefined
) => [MENTION_BOOST_CAMPAIGN_CONFLICT_QUERY_KEY_PREFIX, mentionId, campaignId ?? null];

const fetchMentionBoostCampaignConflict = async (
  mentionId: string,
  campaignId: string
): Promise<MentionBoostCampaignConflictResponse> => {
  const response = await api.get<ApiResponse<MentionBoostCampaignConflictResponse>>(
    `/dashboard/mentions/${mentionId}/boost-campaign-conflict`,
    { params: { campaignId } }
  );

  return response.data.data ?? { conflict: null };
};

interface UseMentionBoostCampaignConflictQueryOptions {
  mentionId: string | null | undefined;
  campaignId: string | null | undefined;
  enabled?: boolean;
}

/**
 * Whether the same post is already boosted in the selected campaign. Advisory
 * only — it never blocks a launch — so it runs solely on an actual selection
 * and stays silent on failure.
 */
export const useMentionBoostCampaignConflictQuery = ({
  mentionId,
  campaignId,
  enabled = true,
}: UseMentionBoostCampaignConflictQueryOptions): UseQueryResult<
  MentionBoostCampaignConflictResponse,
  Error
> => {
  return useQuery<MentionBoostCampaignConflictResponse, Error>({
    queryKey: mentionBoostCampaignConflictQueryKey(mentionId, campaignId),
    queryFn: () => {
      if (!mentionId || !campaignId) {
        return Promise.reject(new Error('Mention ID and campaign ID are required.'));
      }
      return fetchMentionBoostCampaignConflict(mentionId, campaignId);
    },
    enabled: !!mentionId && !!campaignId && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
  });
};

export { fetchMentionBoostCampaignConflict };
